/**
 * ManualComposer — Story 12.3 / Epic E12.
 *
 * Textarea + botão Enviar, visível apenas quando `status === 'human_handoff'`.
 * Submit dispara POST /admin/conversations/:id/messages, que envia via
 * WhatsApp + persiste outbound com metadata `sent_by:'human'`.
 *
 * Atalho de teclado: Cmd/Ctrl + Enter envia (padrão Slack/Discord).
 * Enter sozinho insere nova linha.
 *
 * Error mapping (do BE story 12.2):
 *   - 409 → conversa não está em handoff (race) → refetch silencioso
 *   - 422 → lead opted out → abre modal de confirmação (E12 follow-up
 *     2026-05-26: operador pode forçar envio assumindo a responsabilidade;
 *     audit grava `opt_out_bypass:true`)
 *   - 502 → WhatsApp falhou
 */

import { useState, type KeyboardEvent, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';

const MAX_LENGTH = 1500;

export interface ManualComposerProps {
  conversationId: string;
  /** Disparado após envio com sucesso (refetch da conversa). */
  onSent: () => void;
}

interface SendResponse {
  id: string;
  whatsappMessageId: string;
  sentAt: string;
}

interface SendPayload {
  text: string;
  force?: boolean;
}

function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } } | undefined;
    const code = body?.error?.code;
    if (code === 'WHATSAPP_SEND_FAILED' || err.status === 502) {
      return 'Falha no WhatsApp — tente de novo em alguns segundos.';
    }
    if (code === 'CONVERSATION_NOT_IN_HANDOFF' || err.status === 409) {
      return 'A conversa saiu do handoff — atualizando…';
    }
    if (err.status === 403) return 'Você não tem permissão pra enviar.';
    return body?.error?.message ?? `Falha (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : 'Falha inesperada.';
}

function isOptedOutError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 422) return false;
  const body = err.body as { error?: { code?: string } } | undefined;
  return body?.error?.code === 'LEAD_OPTED_OUT';
}

export function ManualComposer({ conversationId, onSent }: ManualComposerProps) {
  const [text, setText] = useState('');
  // Quando o BE retorna 422 LEAD_OPTED_OUT, em vez de só toast, abrimos
  // modal de confirmação. Se operador confirma, retry com force:true.
  const [optOutModalOpen, setOptOutModalOpen] = useState(false);
  const [pendingText, setPendingText] = useState<string | null>(null);

  const sendMutation = useMutation({
    mutationFn: (body: SendPayload) =>
      apiClient.post<SendResponse>(`/admin/conversations/${conversationId}/messages`, body),
    onSuccess: () => {
      setText('');
      setOptOutModalOpen(false);
      setPendingText(null);
      onSent();
    },
    onError: (err, body) => {
      if (isOptedOutError(err)) {
        // Guarda o texto que foi tentado pra retry após confirmação.
        // body já contém o `text` original (sem force ainda).
        setPendingText(body.text);
        setOptOutModalOpen(true);
        return;
      }
      toast.error(humanizeError(err));
      // Em 409 (race), refetch via onSent pra UI mostrar estado atual.
      if (err instanceof ApiError && err.status === 409) onSent();
    },
  });

  const trimmedLength = text.trim().length;
  const canSend = trimmedLength > 0 && trimmedLength <= MAX_LENGTH && !sendMutation.isPending;

  const handleSubmit = () => {
    if (!canSend) return;
    sendMutation.mutate({ text: text.trim() });
  };

  const handleConfirmForce = () => {
    if (pendingText === null) return;
    sendMutation.mutate({ text: pendingText, force: true });
  };

  const handleCancelForce = () => {
    setOptOutModalOpen(false);
    setPendingText(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter envia. Enter sozinho cria nova linha (default).
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // Autoresize 1-6 linhas
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 6 * 24)}px`;
  };

  return (
    <>
      <div
        data-testid="manual-composer"
        className="sticky bottom-0 mt-4 rounded-md border bg-card p-3 shadow-sm"
      >
        <label htmlFor="manual-composer-textarea" className="sr-only">
          Mensagem manual a enviar via WhatsApp
        </label>
        <textarea
          id="manual-composer-textarea"
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          maxLength={MAX_LENGTH}
          rows={1}
          placeholder="Digite sua mensagem. Cmd/Ctrl + Enter pra enviar."
          className="w-full resize-none border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
          disabled={sendMutation.isPending}
          data-testid="manual-composer-textarea"
        />
        <div className="mt-2 flex items-center justify-between border-t pt-2 text-xs">
          <span className="text-muted-foreground">
            Sua mensagem sai como Rafa pro lead — o nome interno fica gravado pra auditoria.
          </span>
          <div className="flex items-center gap-3">
            <span
              className={
                text.length > MAX_LENGTH - 100
                  ? 'font-medium text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
              }
              aria-live="polite"
            >
              {text.length}/{MAX_LENGTH}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSend}
              data-testid="manual-composer-send"
              aria-label="Enviar mensagem manual"
            >
              <Send aria-hidden className="mr-1 h-3 w-3" />
              {sendMutation.isPending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        open={optOutModalOpen}
        onOpenChange={(open) => {
          if (!open) handleCancelForce();
        }}
      >
        <DialogContent data-testid="opt-out-bypass-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle aria-hidden className="h-5 w-5" />
              Lead descadastrado
            </DialogTitle>
            <DialogDescription className="space-y-2 text-sm">
              <span className="block">
                Esse lead se descadastrou (opt-out). Por padrão, o sistema bloqueia envios pra ele —
                proteção LGPD.
              </span>
              <span className="block">
                Se você tem certeza de que o contato é apropriado (ex: o lead voltou por outro canal
                e pediu retorno), pode forçar o envio.{' '}
                <strong>A ação vai ficar registrada na auditoria com seu nome.</strong>
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelForce}
              disabled={sendMutation.isPending}
              data-testid="opt-out-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmForce}
              disabled={sendMutation.isPending}
              data-testid="opt-out-confirm"
            >
              {sendMutation.isPending ? 'Enviando…' : 'Sim, enviar mesmo assim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ManualComposer;
